import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Smartphone, Copy, Mail, Loader2 } from "lucide-react";

export function MfaProgress({ users, onlyMissing, setOnlyMissing }) {
  const active = users.filter((u) => u.active);
  const done = active.filter((u) => u.mfa_enabled).length;
  const missing = active.filter((u) => !u.mfa_enabled);
  const pct = active.length ? Math.round((done / active.length) * 100) : 0;
  const [sending, setSending] = useState(false);
  const remind = async () => {
    if (!window.confirm(`Email a setup reminder to ${missing.length} staff who haven't set up their authenticator? Anyone reminded in the last 24 hours is skipped.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/users/mfa-reminders");
      toast.success(`Reminder sent to ${data.sent} staff${data.skipped_recent ? ` (${data.skipped_recent} already reminded in the last 24 hours)` : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reminders"); }
    finally { setSending(false); }
  };
  const copy = () => {
    navigator.clipboard?.writeText(missing.map((u) => u.email).join("; "));
    toast.success(`${missing.length} email address${missing.length === 1 ? "" : "es"} copied`);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="mfa-progress">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Smartphone className="w-4 h-4" /></span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900" data-testid="mfa-progress-count">
            {done} of {active.length} active staff have set up their authenticator
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
      {missing.length > 0 && (
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => setOnlyMissing((v) => !v)} data-testid="mfa-progress-filter"
            className={`h-8 px-3 rounded-lg text-xs font-semibold border transition-colors ${onlyMissing ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            {onlyMissing ? "Show all staff" : `Show ${missing.length} not set up`}
          </button>
          <button type="button" onClick={copy} data-testid="mfa-progress-copy"
            className="h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
            <Copy className="w-3.5 h-3.5" /> Copy emails
          </button>
          <button type="button" onClick={remind} disabled={sending} data-testid="mfa-progress-remind"
            className="h-8 px-3 rounded-lg text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white inline-flex items-center gap-1 disabled:opacity-60">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Send reminder
          </button>
        </div>
      )}
    </div>
  );
}
